import type { JSX, ReactNode } from 'react'
import { ResizableHandle } from '#src/components/core/resizable/ResizableHandle.tsx'
import { ResizablePanel } from '#src/components/core/resizable/ResizablePanel.tsx'
import { ResizablePanelGroup } from '#src/components/core/resizable/ResizablePanelGroup.tsx'

export interface InboxDetailLayoutProps {
  list: ReactNode
  detail: ReactNode
  autoSaveId?: string
  listDefaultSize?: number
  detailDefaultSize?: number
  /* The list pane's floor, as a percentage of the group. Per-surface rather
   * than one shared number because the panes are not equivalent: an inbox list
   * is the surface's primary control and stays usable, while the desk list is
   * a secondary index beside a note the user is reading, so that one is allowed
   * all the way to 0.
   *
   * 0 is safe here only because the handle is not inside the pane it sizes: it
   * is a sibling flex child of fixed width, so it survives the pane reaching 0
   * and stays draggable — with 5px of pointer margin either side (15px on a
   * coarse pointer) and arrow-key resizing once focused, both from
   * react-resizable-panels. A collapsed pane is therefore recoverable, which is
   * what makes the floor optional rather than load-bearing. */
  listMinSize?: number
}

export function InboxDetailLayout({
  list,
  detail,
  autoSaveId,
  listDefaultSize = 33,
  detailDefaultSize = 67,
  listMinSize = 20,
}: InboxDetailLayoutProps): JSX.Element {
  const hasDetail = detail !== null && detail !== undefined

  return (
    <ResizablePanelGroup
      autoSaveId={autoSaveId}
      direction="horizontal"
      className="size-full"
    >
      <ResizablePanel
        id="inbox-list"
        defaultSize={hasDetail ? listDefaultSize : 100}
        minSize={listMinSize}
        order={1}
      >
        {list}
      </ResizablePanel>
      {hasDetail && (
        <>
          <ResizableHandle />
          <ResizablePanel
            id="inbox-detail"
            defaultSize={detailDefaultSize}
            minSize={30}
            order={2}
          >
            {detail}
          </ResizablePanel>
        </>
      )}
    </ResizablePanelGroup>
  )
}
